import { Box, Dialog, DialogContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { ProductForm } from '../../../../components/Forms/Product/Product';

import { useNetworkScope } from '../../../../hooks/useNetworkScope';
import { useUser } from '../../../../hooks/useUser';
import { isInternal } from '../../../../utils';
import { IconChatMsgs, IconClock, IconCross, IconEye, IconGeo, IconSettings, IconSparkles } from '../../../../utils/icons';
import { TabHeader } from '../../../../components/Tabs';
import { BetaPill } from '../../../../components/BetaPill';
import { ISingleProduct } from '../productListView/constants';
import { ProductPdpUrlsPanel, usePdpUrlBrand } from './ProductPdpUrlsPanel';
import { ProductConsumerVoicePanel } from './ProductConsumerVoicePanel';
import { ProductAiPresencePanel } from './ProductAiPresencePanel';
import { ProductAutopilotPanel } from './ProductAutopilotPanel';
import { ProductTimelinePanel } from './ProductTimelinePanel';
import { ProductFaqsPanel } from './ProductFaqsPanel';
import { useProductAutopilot } from './useProductAutopilot';

// The chat glyph defaults to 64px; the tab strip wants the 22px the other
// tab icons use.
const IconFaqs = (props: any) => <IconChatMsgs {...props} width={22} height={22} size={undefined} />;

// Tab indexes — the products list deep-links into Autopilot.
export const PRODUCT_MODAL_TAB = {
    general: 0,
    productPages: 1,
    consumerVoice: 2,
    autopilot: 3,
    timeline: 4,
    aiPresence: 5,
    faqs: 6
} as const;

interface Props {
    product?: ISingleProduct;
    onClose: () => void;
    onComplete: (data?: any) => void;
    initialProductName?: string;
    selectedBrandId?: number | string | null;
    selectedBrandName?: string;
    // Tab to open on (PRODUCT_MODAL_TAB); tabs beyond General need a
    // persisted product and fall back to General otherwise.
    initialTab?: number;
}
export function ProductV2Modal({
    product,
    onClose,
    onComplete,
    initialProductName,
    selectedBrandId,
    selectedBrandName,
    initialTab
}: Props) {
    const scope = useNetworkScope();
    const { role } = useUser();
    // Autopilot, Timeline and FAQs are internal-only for now: brand users never
    // see the tabs, and a deep link into them lands on General.
    const showAutopilot = isInternal(role);
    const showFaqs = isInternal(role);
    const brandId = selectedBrandId != null ? Number(selectedBrandId) : scope.brandId;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const autopilotTab = initialTab === PRODUCT_MODAL_TAB.autopilot || initialTab === PRODUCT_MODAL_TAB.timeline;
    const faqsTab = initialTab === PRODUCT_MODAL_TAB.faqs;
    const tabAllowed = (showAutopilot || !autopilotTab) && (showFaqs || !faqsTab);
    const [selectedTab, setSelectedTab] = useState(product?.desireid && initialTab && tabAllowed ? initialTab : 0);

    const productBrands = (product?.brands as { id: number; name?: string; rmn?: boolean }[]) || [];
    // Autopilot is scoped the way the Product Pages tab scopes a new page:
    // the workspace brand, else the operator's pick (null = API default).
    const autopilotBrandId = usePdpUrlBrand(productBrands).brandId;
    const autopilot = useProductAutopilot(showAutopilot ? product?.desireid || null : null, autopilotBrandId);
    // Consumer Voice runs are stamped with the product's own (manufacturer)
    // brand — regular brand first, mirroring the backend's convention.
    const cvBrandId =
        productBrands.find((b: any) => !b?.rmn)?.id ?? productBrands[0]?.id ?? (brandId || null);

    const tabHeader = [
        {
            value: 0,
            text: "General",
            Icon: IconSettings
        },
        {
            value: 1,
            text: "Product Pages",
            Icon: IconGeo
        },
        {
            value: 2,
            text: "Consumer Voice",
            Icon: IconEye
        },
        {
            value: PRODUCT_MODAL_TAB.aiPresence,
            text: "AI Presence",
            Icon: IconSparkles
        },
        ...(showFaqs
            ? [
                  {
                      value: PRODUCT_MODAL_TAB.faqs,
                      text: "FAQs",
                      Icon: IconFaqs
                  }
              ]
            : []),
        ...(showAutopilot
            ? [
                  {
                      value: PRODUCT_MODAL_TAB.autopilot,
                      text: 'Autopilot',
                      Icon: IconSparkles,
                      badge: <BetaPill />
                  },
                  {
                      value: PRODUCT_MODAL_TAB.timeline,
                      text: 'Timeline',
                      Icon: IconClock
                  }
              ]
            : [])
    ];

    return (
        <Dialog
            open
            fullWidth
            maxWidth="lg"
            onClose={onClose}
            fullScreen={fullScreen}
            scroll="paper"
            PaperProps={{
                sx: {
                    maxHeight: fullScreen ? "100%" : "calc(100% - 64px)",
                    overflow: "hidden"
                }
            }}
        >
            <DialogContent
                className="p-6 dialog-content"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: "0 1 auto",
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" }
                }}
            >
                <Box
                    display="flex"
                    alignItems="cenbter"
                    justifyContent="space-between"
                    paddingBottom={3}
                    className="shrink-0 border-b border-border-token "
                >
                    <Typography variant="h5">{product ? "Product Settings" : "Add Product"}</Typography>
                    <div onClick={onClose} className="cursor-pointer">
                        <IconCross />
                    </div>
                </Box>
                {/* Agentic tab needs a persisted product (desireid) — creation stays single-form */}
                {product?.desireid ? (
                    <>
                        <Box className="shrink-0 pt-4">
                            <TabHeader
                                tabs={tabHeader}
                                selectedTab={selectedTab}
                                setSelectedTab={setSelectedTab}
                                variant="pill"
                            />
                        </Box>
                        <Box
                            sx={{
                                display: selectedTab === 0 ? "flex" : "none",
                                flexDirection: "column"
                            }}
                        >
                            <ProductForm
                                product={product}
                                brandId={brandId}
                                initialProductName={initialProductName}
                                initialBrandName={selectedBrandName}
                                hideBrandFields={selectedBrandId != null}
                                onComplete={(data) => onComplete(data)}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: selectedTab === 1 ? "block" : "none"
                            }}
                        >
                            <ProductPdpUrlsPanel desireid={product.desireid} brands={productBrands} />
                        </Box>
                        <Box
                            sx={{
                                display: selectedTab === 2 ? "block" : "none"
                            }}
                        >
                            <ProductConsumerVoicePanel
                                desireid={product.desireid}
                                productTitle={product.title}
                                brandId={cvBrandId}
                                initialSubreddits={(product as any).cv_subreddits}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: selectedTab === PRODUCT_MODAL_TAB.aiPresence ? "block" : "none"
                            }}
                        >
                            <ProductAiPresencePanel desireid={product.desireid} productTitle={product.title} brandId={cvBrandId} />
                        </Box>
                        {showFaqs && (
                            <Box
                                sx={{
                                    display: selectedTab === PRODUCT_MODAL_TAB.faqs ? "block" : "none"
                                }}
                            >
                                <ProductFaqsPanel desireid={product.desireid} />
                            </Box>
                        )}
                        {showAutopilot && (
                            <>
                                <Box
                                    sx={{
                                        display: selectedTab === PRODUCT_MODAL_TAB.autopilot ? 'block' : 'none'
                                    }}
                                >
                                    <ProductAutopilotPanel desireid={product.desireid} brandId={autopilotBrandId} state={autopilot} />
                                </Box>
                                <Box
                                    sx={{
                                        display: selectedTab === PRODUCT_MODAL_TAB.timeline ? 'block' : 'none'
                                    }}
                                >
                                    <ProductTimelinePanel desireid={product.desireid} brandId={autopilotBrandId} state={autopilot} />
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <ProductForm
                        product={product}
                        brandId={brandId}
                        initialProductName={initialProductName}
                        initialBrandName={selectedBrandName}
                        hideBrandFields={selectedBrandId != null}
                        onComplete={(data) => onComplete(data)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
